import React, { useState, useEffect, useContext } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { ArrowLeft, Plus, Save, MapPin, Clock, Trash, Building, Store } from 'lucide-react';
import { AuthContext } from "../../context/AuthContext";
import CheckpointManager from './CheckpointManager';
import ScheduleManager from './ScheduleManager';
import './styles/routeForm.scss';
import { 
  fetchClientesPorAdministrativo, 
  fetchClientesPorInspector, 
  fetchTiendasPorCampoCliente 
} from "../admin/firestoreService";

const RouteForm = ({ route = null, onBack, clienteId = '', servicioId = '' }) => {
  const isEditing = !!route;
  const { currentService, currentUser, adminData } = useContext(AuthContext);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    checkpoints: [],
    schedule: [],
    active: true,
    clienteId: clienteId || '',
    clienteNombre: '',
    servicioId: servicioId || '',
    servicioNombre: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'checkpoints', 'schedule'
  
  // Para seleccionar cliente y servicio
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [filteredServicios, setFilteredServicios] = useState([]);

  // Cargar datos del formulario si estamos editando
  useEffect(() => {
    if (isEditing && route) {
      setFormData({
        name: route.name || '',
        description: route.description || '',
        checkpoints: route.checkpoints || [],
        schedule: route.schedule || [],
        active: typeof route.active === 'boolean' ? route.active : true,
        clienteId: route.clienteId || '',
        clienteNombre: route.clienteNombre || '',
        servicioId: route.servicioId || '',
        servicioNombre: route.servicioNombre || route.servicioNumTienda || ''
      });
    } else if (clienteId || servicioId) {
      // Si nos pasan clienteId o servicioId como props, actualizamos el formData
      setFormData(prevData => ({
        ...prevData,
        clienteId: clienteId || prevData.clienteId,
        servicioId: servicioId || prevData.servicioId
      }));
    }
  }, [isEditing, route, clienteId, servicioId]);

  // Cargar clientes usando firestoreService
  useEffect(() => {
    const loadClientes = async () => {
      try {
        if (currentService?.id && adminData?.id) {
          let clientesList = [];
          
          // Usar las funciones de firestoreService según el tipo de usuario
          if (adminData.tipoUsuario?.includes('administrativo')) {
            clientesList = await fetchClientesPorAdministrativo(adminData.id, currentService);
          } else if (adminData.tipoUsuario?.includes('inspector')) {
            clientesList = await fetchClientesPorInspector(adminData.id, currentService);
          } else if (adminData.tipoUsuario?.includes('administrador')) {
            // Si es administrador, cargar todos los clientes
            const { fetchClientesYTiendas } = await import("../admin/firestoreService");
            const [clientesData] = await fetchClientesYTiendas(currentService);
            clientesList = clientesData;
          }
          
          setClientes(clientesList);
          
          // Si tenemos un clienteId en formData, buscar el nombre
          if (formData.clienteId && !formData.clienteNombre) {
            const clienteDoc = clientesList.find(cliente => cliente.id === formData.clienteId);
            
            if (clienteDoc) {
              setFormData(prev => ({
                ...prev,
                clienteNombre: clienteDoc.nombre || ''
              }));
            }
          }
        }
      } catch (error) {
        console.error("Error al cargar clientes:", error);
        setClientes([]);
      }
    };

    loadClientes();
  }, [currentService, adminData, formData.clienteId]);

  // Cargar servicios (tiendas) basados en los clientes permitidos
  useEffect(() => {
    const loadServicios = async () => {
      try {
        if (currentService?.id && clientes.length > 0) {
          const clientesIds = clientes.map(c => c.id);
          const serviciosList = await fetchTiendasPorCampoCliente(clientesIds, currentService);
          setServicios(serviciosList);
          
          // Si tenemos un servicioId en formData, buscar el nombre
          if (formData.servicioId && !formData.servicioNombre) {
            const servicio = serviciosList.find(s => s.id === formData.servicioId);
            
            if (servicio) {
              setFormData(prev => ({
                ...prev,
                servicioNombre: servicio.numTienda || servicio.nombre || ''
              }));
            }
          }
        } else {
          setServicios([]);
        }
      } catch (error) {
        console.error("Error al cargar servicios:", error);
        setServicios([]);
      }
    };

    loadServicios();
  }, [currentService, clientes, formData.servicioId]);

  // Filtrar servicios cuando cambia el cliente seleccionado
  useEffect(() => {
    if (formData.clienteId) {
      const filtered = servicios.filter(servicio => 
        servicio.cliente === formData.clienteId || 
        servicio.clienteId === formData.clienteId || 
        servicio.nombreCliente === formData.clienteNombre
      );
      setFilteredServicios(filtered);
      
      // Si el servicio seleccionado no pertenece al cliente, resetearlo
      if (formData.servicioId) {
        const servicioExiste = filtered.some(s => s.id === formData.servicioId);
        if (!servicioExiste) {
          setFormData(prev => ({
            ...prev,
            servicioId: '',
            servicioNombre: ''
          }));
        }
      }
    } else {
      setFilteredServicios(servicios);
    }
  }, [formData.clienteId, formData.clienteNombre, servicios]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleClienteChange = (e) => {
    const clienteId = e.target.value;
    const selectedCliente = clientes.find(c => c.id === clienteId);
    
    setFormData({
      ...formData,
      clienteId: clienteId,
      clienteNombre: selectedCliente ? selectedCliente.nombre : ''
    });
  };

  const handleServicioChange = (e) => {
    const servicioId = e.target.value;
    const selectedServicio = servicios.find(s => s.id === servicioId);
    
    setFormData({
      ...formData,
      servicioId: servicioId,
      servicioNombre: selectedServicio ? (selectedServicio.numTienda || selectedServicio.nombre || '') : ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      setError('El nombre de la ruta es obligatorio');
      return;
    }
    
    // No requerimos puntos de control o horarios para guardar, pero mostramos advertencia
    const warnings = [];
    if (formData.checkpoints.length === 0) {
      warnings.push('No has añadido ningún punto de control.');
    }
    
    if (formData.schedule.length === 0) {
      warnings.push('No has añadido ningún horario.');
    }
    
    if (warnings.length > 0 && !window.confirm(warnings.join('\n') + '\n\n¿Deseas guardar la ruta de todos modos?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const routeData = {
        ...formData,
        updatedAt: serverTimestamp()
      };
      
      if (!isEditing) {
        routeData.createdAt = serverTimestamp();
        // Usar la subcolección de rutas dentro del servicio actual
        await addDoc(collection(db, 'servicios', currentService.id, 'rutas'), routeData);
      } else {
        // Actualizar en la subcolección correcta
        await updateDoc(doc(db, 'servicios', currentService.id, 'rutas', route.id), routeData);
      }
      
      setSuccess(true);
      setLoading(false);
      
      // Mostrar mensaje de éxito brevemente antes de regresar
      setTimeout(() => {
        onBack();
      }, 1500);
      
    } catch (err) {
      console.error('Error saving route:', err);
      setError('Error al guardar la ruta. Por favor, inténtalo de nuevo.');
      setLoading(false);
    }
  };

  const updateCheckpoints = (checkpoints) => {
    setFormData({ ...formData, checkpoints });
  };

  const updateSchedule = (schedule) => {
    setFormData({ ...formData, schedule });
  };

  return (
    <div className="route-form">
      <div className="form-header">
        <button 
          onClick={onBack}
          className="back-button"
          title="Volver"
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="form-title">
          {isEditing ? `Editar Ruta: ${route.name}` : 'Crear Nueva Ruta'}
        </h2>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {isEditing ? 'Ruta actualizada correctamente' : 'Ruta creada correctamente'}
        </div>
      )}

      <div className="form-content">
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            Información General
          </button>
          <button
            className={`tab-button ${activeTab === 'checkpoints' ? 'active' : ''}`}
            onClick={() => setActiveTab('checkpoints')}
          >
            <div className="tab-content">
              <MapPin size={16} className="tab-icon" />
              Puntos de Control
              {formData.checkpoints.length > 0 && (
                <span className="badge">
                  {formData.checkpoints.length}
                </span>
              )}
            </div>
          </button>
          <button
            className={`tab-button ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            <div className="tab-content">
              <Clock size={16} className="tab-icon" />
              Horarios
              {formData.schedule.length > 0 && (
                <span className="badge">
                  {formData.schedule.reduce((total, day) => total + (day.times?.length || 0), 0)}
                </span>
              )}
            </div>
          </button>
        </div>

        <div className="tab-panel">
          {activeTab === 'info' && (
            <div>
              <div className="form-group">
                <label className="form-label">
                  Nombre de la Ruta *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Ej: Ruta Hospital Planta 1"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Descripción
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Descripción de la ruta y sus objetivos..."
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <Building size={16} className="input-icon" />
                  Cliente
                </label>
                <select
                  name="clienteId"
                  value={formData.clienteId}
                  onChange={handleClienteChange}
                  className="form-select"
                >
                  <option value="">Seleccionar Cliente</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <Store size={16} className="input-icon" />
                  Servicio (Tienda)
                </label>
                <select
                  name="servicioId"
                  value={formData.servicioId}
                  onChange={handleServicioChange}
                  className="form-select"
                  disabled={!formData.clienteId}
                >
                  <option value="">Seleccionar Servicio</option>
                  {filteredServicios.map(servicio => (
                    <option key={servicio.id} value={servicio.id}>
                      {servicio.numTienda || servicio.nombre || servicio.id}
                    </option>
                  ))}
                </select>
                {!formData.clienteId && (
                  <p className="form-hint">Primero selecciona un cliente para ver los servicios disponibles</p>
                )}
              </div>
              
              <div className="form-group">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    name="active"
                    id="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <label htmlFor="active" className="checkbox-label">
                    Ruta activa
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'checkpoints' && (
            <CheckpointManager 
              checkpoints={formData.checkpoints} 
              onChange={updateCheckpoints} 
            />
          )}

          {activeTab === 'schedule' && (
            <ScheduleManager 
              schedule={formData.schedule} 
              onChange={updateSchedule} 
            />
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onBack}
            className="btn btn-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="btn btn-save"
          >
            {loading ? (
              'Guardando...'
            ) : (
              <>
                <Save size={18} className="save-icon" />
                {isEditing ? 'Actualizar Ruta' : 'Crear Ruta'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouteForm;